import { useEffect, useRef } from "react"

export function deepEqual(object1: unknown, object2: unknown): boolean {
  if (!isObject(object1) || !isObject(object2)) {
    return object1 === object2
  }

  const obj1 = object1 as Record<string, unknown>
  const obj2 = object2 as Record<string, unknown>
  const keys1 = Object.keys(obj1)
  const keys2 = Object.keys(obj2)

  if (keys1.length !== keys2.length) {
    return false
  }

  for (const key of keys1) {
    const val1 = obj1[key]
    const val2 = obj2[key]
    const areObjects = isObject(val1) && isObject(val2)
    if (
      (areObjects && !deepEqual(val1, val2)) ||
      (!areObjects && val1 !== val2)
    ) {
      return false
    }
  }

  return true
}

export function isObject(object: unknown): boolean {
  return object != null && typeof object === "object"
}

export function getNestedValueFromString(obj: Record<string, unknown>, key: string): unknown {
  const keys = key.split(".") || [""]
  return getNestedValue(obj, keys)
}

export function getNestedValue(obj: Record<string, unknown>, keys: string[]): unknown {
  return keys.reduce((o: unknown, key: string) => {
    if (o && typeof o === "object" && key in (o as object)) {
      return (o as Record<string, unknown>)[key]
    }
    return undefined
  }, obj)
}

/**
 * A useEffect variant that skips execution on the initial mount.
 *
 * Use this when you need to run side effects only when dependencies change,
 * but not on the first render. Common use cases:
 * - Triggering API calls when filter/search params change (but not on page load)
 * - Syncing state changes to external systems after user interaction
 * - Avoiding duplicate requests when initial data comes from server props
 *
 * @param fn - The effect callback to run when dependencies change (after mount)
 * @param dependencies - Array of values that trigger the effect when changed
 *
 * @example
 * // Fetch data when filters change, but not on initial render
 * useEffectAfterMount(() => {
 *   router.get("/items", { filters })
 * }, [JSON.stringify(filters)])
 */
export const useEffectAfterMount = (fn: () => void, dependencies: unknown[]) => {
  const didMount = useRef(false)

  // Reset ref on unmount for React StrictMode compatibility
  useEffect(() => {
    return () => {
      didMount.current = false
    }
  }, [])

  useEffect(() => {
    if (didMount.current) {
      fn()
    } else {
      didMount.current = true
    }
  }, dependencies)
}

/**
 * A debounced version of useEffectAfterMount.
 *
 * Skips the initial mount AND debounces subsequent executions. Useful for
 * expensive operations triggered by rapid user input (e.g., search-as-you-type).
 *
 * @param fn - The effect callback to run (debounced, after mount)
 * @param dependencies - Array of values that trigger the effect when changed
 * @param delay - Debounce delay in milliseconds (default: 300ms)
 *
 * @example
 * // Search API call debounced while user types
 * useEffectAfterMountDebounced(() => {
 *   searchAPI(query)
 * }, [query], 500)
 */
export const useEffectAfterMountDebounced = (fn: () => void, dependencies: unknown[], delay = 300) => {
  const didMount = useRef(false)

  // Reset ref on unmount for React StrictMode compatibility
  useEffect(() => {
    return () => {
      didMount.current = false
    }
  }, [])

  useEffect(() => {
    if (didMount.current) {
      const handler = setTimeout(() => {
        fn()
      }, delay)

      return () => {
        clearTimeout(handler)
      }
    } else {
      didMount.current = true
    }
  }, dependencies)
}
